import { z } from "zod";

import {
  genderEnum,
  breedEnum,
  ageTypeEnum,
  birthdayMonthEnum,
  ageModeEnum,
  catWeightUnitEnum,
  catBodyTypeEnum,
  catActivityLevelEnum,
  catFussinessEnum,
  catFoodTypeBeforeEnum,
  catBrandBeforeEnum,
  allergenEnum,
  catMedicalConditionEnum,
  flavourEnum,
  textureEnum,
} from "../types/enums";
import { migrateLegacyCatFitness } from "../helpers/legacy-cat-fitness";
import {
  recipeSelectionSchema,
  treatItemSchema,
  defaultFalseSchema,
} from "./primitives";

const catFormDataObjectSchema = z.object({
  // User-entered identity fields
  name: z.string().optional(),
  gender: genderEnum.optional(),
  breed: breedEnum.optional(),
  lifeStage: ageTypeEnum.optional(),
  birthdayMonth: birthdayMonthEnum.optional(),
  birthday: z.string().optional(),
  isNeutered: z.boolean().optional(),

  age: z
    .object({
      years: z.number().optional(),
      months: z.number().optional(),
      weeks: z.number().optional(),
      mode: ageModeEnum.optional(),
    })
    .optional(),

  weight: z
    .object({
      valueInKg: z.number().optional(),
      valueInLb: z.number().optional(),
      unit: catWeightUnitEnum.default("KG"),
      hasUserEditedValue: defaultFalseSchema,
    })
    .optional(),

  body: z
    .object({
      bodyType: catBodyTypeEnum.optional(),
      activityLevel: catActivityLevelEnum.optional(),
    })
    .optional(),

  diet: z
    .object({
      eatingHabits: catFussinessEnum.optional(),
      preferences: z
        .object({
          flavours: z.array(flavourEnum).optional(),
          textures: z.array(textureEnum).optional(),
        })
        .optional(),
      current: z
        .object({
          type: z.array(catFoodTypeBeforeEnum).optional(),
          brand: catBrandBeforeEnum.optional(),
        })
        .optional(),
      foodAllergies: z
        .object({
          hasAllergies: z.boolean().optional(),
          allergens: z.array(allergenEnum).optional(),
        })
        .optional(),
    })
    .optional(),

  medical: z
    .object({
      hasConditions: z.boolean().optional(),
      conditions: z.array(catMedicalConditionEnum).optional(),
    })
    .optional(),

  // Structural fields
  softDelete: defaultFalseSchema,

  // Computed / API-derived fields
  // Trial discounted meal plan price/day (excludes nibbles/extras).
  trialPricePerDay: z.string().optional(),
  // Trial original (RRP) meal plan price/day (excludes nibbles/extras, no MC).
  trialOriginalPricePerDay: z.string().optional(),
  // Ongoing discounted meal plan price/day (excludes nibbles/extras, can include MC).
  ongoingPricePerDay: z.string().optional(),
  calculatedCalories: z.number().optional(),

  // User selections
  selectedRecipes: recipeSelectionSchema.optional(),
  freeGiftTreat: treatItemSchema.optional(),
});

// Preprocess to map any legacy body condition and activity level values to the new enum values.
export const catFormDataSchema = z.preprocess(
  migrateLegacyCatFitness,
  catFormDataObjectSchema,
);

export type TCatFormData = z.infer<typeof catFormDataObjectSchema>;
export type TCatFormDataInput = z.input<typeof catFormDataObjectSchema>;
export type TCatEntry = { id: string; cat: Partial<TCatFormDataInput> };
